import React, { useState } from 'react';
import { Search, Loader2, Sparkles, Zap, Building2, Stethoscope, ShoppingCart, Headset, Scissors } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const TemplatesPage = ({ embedded = false }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(null);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  const handleUseTemplate = async (templateId) => {
    console.log('handleUseTemplate triggered:', { templateId, user: user?.id });
    if (!user) {
      console.log('No user session found, triggering login...');
      login();
      return;
    }

    setImporting(templateId);
    try {
      console.log('Making POST request to /api/bots/import-template...');
      const response = await api.post('/api/bots/import-template', { template_id: templateId });
      console.log('API Response status:', response.status);

      if (response.ok) {
        const newBot = await response.json();
        console.log('Template imported successfully:', newBot);
        navigate(`/dashboard/bots/${newBot.bot_id}/flow`);
      } else {
        const err = await response.json();
        console.error('Failed to import template (backend error):', err);
        alert(`Erro: ${err.detail || 'Falha ao importar'}`);
      }
    } catch (error) {
      console.error('Error importing template (exception):', error);
      alert('Erro inesperado ao conectar com o servidor.');
    } finally {
      setImporting(null);
    }
  };

  const categories = [
    { id: 'all', name: 'Todos', icon: <Sparkles className="h-4 w-4" /> },
    { id: 'realestate', name: 'Imobiliária', icon: <Building2 className="h-4 w-4" /> },
    { id: 'healthcare', name: 'Saúde', icon: <Stethoscope className="h-4 w-4" /> },
    { id: 'ecommerce', name: 'E-commerce', icon: <ShoppingCart className="h-4 w-4" /> },
    { id: 'support', name: 'Suporte', icon: <Headset className="h-4 w-4" /> },
    { id: 'beauty', name: 'Beleza', icon: <Scissors className="h-4 w-4" /> },
  ];

  const templatesList = [
    // Imobiliária
    {
      id: 'tpl_realestate_rental',
      name: 'Imobiliária Pro',
      description: 'Capte e qualifique leads de imóveis automaticamente. Agenda visitas e envia detalhes do imóvel.',
      category: 'realestate',
      channel: 'WhatsApp',
      badge: 'Chatfuel Logic'
    },
    {
      id: 'tpl_realestate_sales',
      name: 'Vendas de Imóveis',
      description: 'Bot especializado em venda de imóveis. Filtra por região, preço e tipo de imóvel.',
      category: 'realestate',
      channel: 'WhatsApp',
      badge: 'Lead Gen'
    },
    // Saúde
    {
      id: 'tpl_health_booking',
      name: 'Agendamento Médico',
      description: 'Automatize triagem e agendamento de consultas. Ideal para clínicas e consultórios.',
      category: 'healthcare',
      channel: 'WhatsApp',
      badge: 'Jotform Style'
    },
    {
      id: 'tpl_health_followup',
      name: 'Follow-up de Pacientes',
      description: 'Envie lembretes de consulta, resultados de exames e instruções pós-atendimento.',
      category: 'healthcare',
      channel: 'WhatsApp',
      badge: 'Automation'
    },
    {
      id: 'tpl_health_dental',
      name: 'Clínica Odontológica',
      description: 'Agendamento de consultas, FAQ sobre procedimentos e lembretes de retorno.',
      category: 'healthcare',
      channel: 'WhatsApp',
      badge: 'Booking'
    },
    // E-commerce
    {
      id: 'tpl_ecommerce_luxury',
      name: 'E-commerce: Vendas & Cupons',
      description: 'Impulsione vendas com recomendações personalizadas e cupons de desconto automáticos.',
      category: 'ecommerce',
      channel: 'Website',
      badge: 'AI Powered'
    },
    {
      id: 'tpl_ecommerce_cart',
      name: 'Recuperação de Carrinho',
      description: 'Recupere carrinhos abandonados enviando lembretes e ofertas especiais por WhatsApp.',
      category: 'ecommerce',
      channel: 'WhatsApp',
      badge: 'Conversão'
    },
    {
      id: 'tpl_ecommerce_catalog',
      name: 'Catálogo de Produtos',
      description: 'Apresente seus produtos via chat com imagens, preços e botão de compra direto.',
      category: 'ecommerce',
      channel: 'Facebook',
      badge: 'Catalog'
    },
    // Suporte
    {
      id: 'tpl_customer_support_advanced',
      name: 'Suporte Técnico & FAQ',
      description: 'Resolva dúvidas frequentes e escale casos complexos para humanos automaticamente.',
      category: 'support',
      channel: 'Facebook',
      badge: 'Automation'
    },
    {
      id: 'tpl_support_ticket',
      name: 'Sistema de Tickets',
      description: 'Crie tickets de suporte, acompanhe status e notifique o cliente sobre atualizações.',
      category: 'support',
      channel: 'Website',
      badge: 'Helpdesk'
    },
    // Beleza
    {
      id: 'tpl_beauty_salon',
      name: 'Salão de Beleza & Spa',
      description: 'Gestão completa de serviços e agendamentos por WhatsApp para salões e spas.',
      category: 'beauty',
      channel: 'WhatsApp',
      badge: 'Booking'
    },
    {
      id: 'tpl_beauty_aesthetic',
      name: 'Clínica de Estética',
      description: 'Agendamento de procedimentos estéticos, avaliação prévia e follow-up pós-tratamento.',
      category: 'beauty',
      channel: 'Instagram',
      badge: 'Premium'
    },
  ];

  const filteredTemplates = templatesList.filter(template => {
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className={embedded ? "space-y-6" : "min-h-screen bg-[#f8fafc]"}>
      {!embedded && <Navbar />}

      <main className={embedded ? "" : "pt-28 pb-16"}>
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          {!embedded && (
            <div className="flex justify-center mb-4">
              <span className="bg-blue-100 text-blue-600 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-2">
                <Zap className="h-3 w-3 fill-current" /> Marketplace de Templates
              </span>
            </div>
          )}
          <h1 className={`font-bold text-slate-900 mb-4 tracking-tight ${embedded ? 'text-2xl' : 'text-4xl md:text-5xl'}`}>
            {embedded ? 'Templates Disponíveis' : <>Potencialize seu negócio com <span className="text-blue-600">IA</span></>}
          </h1>
          <p className={`${embedded ? 'text-base' : 'text-xl'} text-slate-500 mb-8 max-w-2xl mx-auto`}>
            Selecione um template otimizado por especialistas para começar a receber leads e pedidos hoje mesmo.
          </p>

          {/* Search */}
          <div className="max-w-xl mx-auto relative group">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Que tipo de bot você precisa?"
              className="pl-12 h-14 rounded-2xl border-slate-200 shadow-sm focus:ring-blue-500 focus:border-blue-500 text-lg"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl text-sm font-semibold transition-all border ${selectedCategory === category.id
                  ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-100 scale-105'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-blue-200 hover:bg-slate-50'
                  }`}
              >
                {category.icon}
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTemplates.map((template) => (
              <Card key={template.id} className="border-none shadow-sm hover:shadow-xl transition-all duration-300 rounded-3xl overflow-hidden group bg-white border border-transparent hover:border-blue-100">
                <div className="h-3 bg-gradient-to-r from-blue-400 to-indigo-500" />
                <CardContent className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <span className="px-3 py-1 bg-slate-100 text-slate-600 rounded-lg text-xs font-bold uppercase tracking-wider">
                      {template.channel}
                    </span>
                    {template.badge && (
                      <span className="bg-blue-50 text-blue-600 text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter border border-blue-100">
                        {template.badge}
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-blue-600 transition-colors">
                    {template.name}
                  </h3>
                  <p className="text-slate-500 text-sm leading-relaxed mb-8 h-12 overflow-hidden line-clamp-2">
                    {template.description}
                  </p>
                  <Button
                    onClick={() => handleUseTemplate(template.id)}
                    disabled={importing === template.id}
                    className="w-full h-12 rounded-xl bg-slate-900 hover:bg-blue-600 text-white font-bold transition-all group-hover:translate-y-[-2px]"
                  >
                    {importing === template.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      'Usar este template'
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredTemplates.length === 0 && (
            <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-slate-200">
              <p className="text-slate-400 font-medium">Não encontramos templates que correspondam à sua busca.</p>
              <Button
                variant="link"
                onClick={() => { setSelectedCategory('all'); setSearchTerm(''); }}
                className="text-blue-600 mt-2"
              >
                Ver todos os templates
              </Button>
            </div>
          )}
        </div>
      </main>

      {!embedded && (
        <div className="bg-slate-900 py-16">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Precisa de algo personalizado?</h2>
            <p className="text-slate-400 mb-8">Nossos especialistas podem criar uma automação personalizada para o seu negócio.</p>
            <Button className="bg-blue-600 hover:bg-blue-700 h-12 px-8 rounded-xl font-bold">
              Falar com Suporte
            </Button>
          </div>
        </div>
      )}

      {!embedded && <Footer />}
    </div>
  );
};

export default TemplatesPage;
